import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Alert, Badge, Spinner } from "react-bootstrap";
import {
  IconCreditCard,
  IconCheck,
  IconAlertTriangle,
  IconCalendar,
  IconCrown,
  IconRefresh,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { setCookie } from "nookies";
import PlanServices from "../../../services/PlanServices";
import CustomerBillingProfileServices from "../../../services/CustomerBillingProfileServices";
import UserContext from "../../../contexts/UserContext";

/**
 * Modal para regularização de plano expirado/suspenso/cancelado.
 * Verifica billing profile e processa o pagamento para reativar o plano.
 */
const RegularizePlanModal = ({ show, onHide, reason, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Estados de dados
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planFeatures, setPlanFeatures] = useState([]);

  // Estados de loading
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [checkingBillingProfile, setCheckingBillingProfile] = useState(true);

  // Estados de billing
  const [hasBillingProfile, setHasBillingProfile] = useState(false);

  // Estados de resultado
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);

  // Estado de aceite dos termos
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Mensagens de erro específicas da API
  const ERROR_MESSAGES = {
    CUSTOMER_PROFILE_NOT_FOUND: "Perfil de cliente não encontrado. Configure seu perfil primeiro.",
    BILLING_PROFILE_NOT_FOUND: "Nenhum método de pagamento cadastrado.",
    CARD_TOKEN_NOT_FOUND: "Token do cartão não encontrado. Por favor, recadastre seu cartão.",
  };

  // Mensagens baseadas no motivo da restrição
  const REASON_MESSAGES = {
    expired: {
      title: "Plano Expirado",
      description: "Seu plano expirou. Regularize agora para continuar usando todas as funcionalidades.",
      variant: "danger",
    },
    suspended: {
      title: "Pagamento Pendente",
      description: "Seu plano está suspenso por falta de pagamento. Regularize para evitar a perda de acesso.",
      variant: "warning",
    },
    canceled: {
      title: "Plano Cancelado",
      description: "Seu plano foi cancelado. Reative agora para voltar a usar a plataforma.",
      variant: "danger",
    },
    no_plan: {
      title: "Sem Plano Ativo",
      description: "Você não possui um plano ativo. Contrate um plano para começar a usar a plataforma.",
      variant: "info",
    },
  };

  // Carregar dados quando o modal abre
  useEffect(() => {
    if (show) {
      loadInitialData();
      // Reset states
      setAcceptedTerms(false);
      setSubscribeResult(null);
      setSubscribeError(null);
    }
  }, [show]);

  const loadInitialData = async () => {
    setLoadingPlan(true);
    setCheckingBillingProfile(true);

    try {
      await Promise.all([
        loadCurrentPlan(),
        checkBillingProfile(),
      ]);
    } catch (error) {
      console.error("Erro ao carregar dados iniciais:", error);
    }
  };

  const loadCurrentPlan = async () => {
    try {
      const response = await PlanServices.getCurrentPlan();
      if (response.data) {
        const { plan, features } = response.data;
        setCurrentPlan({
          id: plan.id,
          name: plan.name,
          monthly_value: plan.monthly_value,
          status: PlanServices.mapPlanStatus(plan.status),
          expiration_date: plan.expiration_date,
          aquisition_date: plan.aquisition_date,
        });
        setPlanFeatures(features || []);
      }
    } catch (error) {
      console.error("Erro ao carregar plano atual:", error);
      // Fallback para plano padrão
      setCurrentPlan({
        id: 3,
        name: "OnDemand",
        monthly_value: 0,
        status: "EXPIRED",
      });
    } finally {
      setLoadingPlan(false);
    }
  };

  const checkBillingProfile = async () => {
    try {
      const response = await CustomerBillingProfileServices.listCustomerBillingProfiles({
        page_size: 1,
        is_active: true,
      });

      const hasProfile = response.data?.results?.length > 0 || response.data?.count > 0;
      setHasBillingProfile(hasProfile);
    } catch (error) {
      console.error("Erro ao verificar perfil de faturamento:", error);
      setHasBillingProfile(false);
    } finally {
      setCheckingBillingProfile(false);
    }
  };

  const handleConfirm = async () => {
    if (!acceptedTerms || !currentPlan?.id) return;

    setLoading(true);
    setSubscribeError(null);

    try {
      const response = await PlanServices.subscribePlan(currentPlan.id);

      if (response.data?.success) {
        // Atualizar o token JWT se retornado pela API
        if (response.data.auth?.access) {
          setCookie(undefined, "token", response.data.auth.access, {
            maxAge: 60 * 60 * 24 * 7, // 7 dias
            path: "/",
          });
        }

        setSubscribeResult(response.data);
      }
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = "Erro ao processar pagamento. Tente novamente.";

      // Verificar erros específicos
      if (errorData?.error_code && ERROR_MESSAGES[errorData.error_code]) {
        errorMessage = ERROR_MESSAGES[errorData.error_code];
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }

      setSubscribeError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToBilling = () => {
    onHide();
    navigate("/billing");
  };

  const handleClose = () => {
    if (subscribeResult && onSuccess) {
      onSuccess();
    }
    setSubscribeResult(null);
    setSubscribeError(null);
    onHide();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  };

  const reasonConfig = REASON_MESSAGES[reason] || REASON_MESSAGES.no_plan;

  // Renderizar estado de carregamento inicial
  if (checkingBillingProfile || loadingPlan) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="mb-0">Carregando informações do plano...</p>
        </Modal.Body>
      </Modal>
    );
  }

  // Renderizar resultado de sucesso
  if (subscribeResult) {
    return (
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="border-0 bg-success">
          <Modal.Title className="d-flex align-items-center text-white">
            <IconCheck size={28} className="me-2" />
            Plano Regularizado com Sucesso!
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="success" className="mb-4">
            <div className="d-flex align-items-center">
              <IconCrown size={24} className="me-3 flex-shrink-0" />
              <div>
                <strong>{subscribeResult.message}</strong>
                <p className="mb-0 mt-1">
                  Seu plano <strong>{subscribeResult.plan?.name}</strong> foi reativado com sucesso.
                </p>
              </div>
            </div>
          </Alert>

          {/* Resumo da transação */}
          <div className="bg-light rounded p-4 mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <IconCreditCard size={18} className="me-2" />
              Resumo da Cobranca
            </h6>
            <div className="row g-3">
              <div className="col-6">
                <small className="text-muted d-block">Plano</small>
                <strong className="text-primary">{subscribeResult.plan?.name}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Valor mensal</small>
                <strong>{PlanServices.formatCurrency(subscribeResult.plan?.value)}</strong>
              </div>
            </div>
          </div>

          {/* Informações da assinatura */}
          {subscribeResult.subscription && (
            <div className="bg-primary bg-opacity-10 rounded p-4 mb-4">
              <h6 className="mb-3">Sua Assinatura</h6>
              <div className="row g-2">
                <div className="col-6">
                  <small className="text-muted d-block">Valor mensal</small>
                  <strong>
                    {PlanServices.formatCurrency(subscribeResult.subscription.monthly_value)}
                  </strong>
                </div>
                <div className="col-6">
                  <small className="text-muted d-block">Proximo vencimento</small>
                  <strong>{formatDate(subscribeResult.subscription.next_due_date)}</strong>
                </div>
              </div>
            </div>
          )}

          {/* Status da conta */}
          {/* {subscribeResult.account && (
            <Alert variant="info" className="mb-0">
              <IconCheck size={18} className="me-2" />
              Sua conta esta ativa ate <strong>{formatDate(subscribeResult.account.plan_expiration)}</strong>.
              Aproveite todos os recursos do plano!
            </Alert>
          )} */}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="success" onClick={handleClose} size="lg">
            <IconCheck size={18} className="me-2" />
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar mensagem de perfil de faturamento não encontrado
  if (!hasBillingProfile) {
    return (
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <IconCreditCard size={28} className="text-warning me-2" />
            Perfil de Faturamento Necessario
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant={reasonConfig.variant} className="mb-4">
            <div className="d-flex align-items-start">
              <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
              <div>
                <strong>{reasonConfig.title}</strong>
                <p className="mb-0 mt-2">{reasonConfig.description}</p>
              </div>
            </div>
          </Alert>

          <Alert variant="warning" className="mb-4">
            <div className="d-flex align-items-start">
              <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
              <div>
                <strong>Atencao!</strong>
                <p className="mb-0 mt-2">
                  Para regularizar seu plano, voce precisa primeiro cadastrar
                  um perfil de faturamento com os dados de pagamento.
                </p>
              </div>
            </div>
          </Alert>

          <div className="text-center py-3">
            <IconCreditCard size={64} className="text-muted mb-3" />
            <h5>Cadastre seu perfil de faturamento</h5>
            <p className="text-muted mb-0">
              Adicione um cartao de credito ou outro meio de pagamento para continuar.
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleGoToBilling}>
            <IconCreditCard size={18} className="me-2" />
            Ir para Faturamento
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Renderizar modal de confirmação de regularização
  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <IconRefresh size={28} className="text-primary me-2" />
          Regularizar Plano
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Alerta sobre o motivo da restrição */}
        <Alert variant={reasonConfig.variant} className="mb-4">
          <div className="d-flex align-items-start">
            <IconAlertTriangle size={24} className="me-3 flex-shrink-0 mt-1" />
            <div>
              <strong>{reasonConfig.title}</strong>
              <p className="mb-0 mt-2">{reasonConfig.description}</p>
            </div>
          </div>
        </Alert>

        {/* Resumo do plano atual */}
        {currentPlan && (
          <div className="bg-light rounded p-4 mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <IconCrown size={18} className="me-2" />
              Resumo do Plano
            </h6>
            <div className="row g-3">
              <div className="col-6">
                <small className="text-muted d-block">Plano</small>
                <strong className="text-primary">{currentPlan.name}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Valor mensal</small>
                <strong>{PlanServices.formatCurrency(currentPlan.monthly_value)}</strong>
              </div>
              {currentPlan.expiration_date && (
                <div className="col-6">
                  <small className="text-muted d-block">Data de expiracao</small>
                  <div className="d-flex align-items-center">
                    <IconCalendar size={16} className="me-1 text-muted" />
                    <strong className="text-danger">{formatDate(currentPlan.expiration_date)}</strong>
                  </div>
                </div>
              )}
              <div className="col-6">
                <small className="text-muted d-block">Status</small>
                <Badge bg={PlanServices.getPlanStatusVariant(currentPlan.status)}>
                  {PlanServices.getPlanStatusText(currentPlan.status)}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Erro de contratação */}
        {subscribeError && (
          <Alert variant="danger" className="mb-4">
            <IconAlertTriangle size={18} className="me-2" />
            {subscribeError}
          </Alert>
        )}

        {/* Aceite dos termos */}
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="accept-terms-regularize"
            label={
              <span>
                Li e aceito os{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  termos de uso
                </a>{" "}
                e a{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  politica de privacidade
                </a>
              </span>
            }
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!acceptedTerms || loading || !currentPlan?.id}
          className="d-flex align-items-center"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processando...
            </>
          ) : (
            <>
              <IconCheck size={18} className="me-2" />
              Confirmar Pagamento
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegularizePlanModal;
